import { BookOpen, CheckSquare, Download, FileUp, Plus, Search, Trash2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { deleteTrades, useAction } from "wasp/client/operations";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../client/components/ui/dialog";
import { useToast } from "../../client/hooks/use-toast";
import { trackBulkTradeDeleted } from "../../client/utils/posthog";
import { Button } from "../../client/components/ui/button";
import { Input } from "../../client/components/ui/input";
import useTranslation from "../../client/hooks/useTranslation";
import { exportTradesToCSV } from "../../trading-journal/csvImport";
import { useLiteData } from "../data/LiteDataProvider";
import { dayKey } from "../lib/format";
import { net, summarize } from "../lib/stats";
import type { LiteTrade } from "../lib/types";
import { EmptyStart } from "../onboarding/EmptyStart";
import { useLiteActions } from "../shell/LiteActions";
import { PageBody, PageHeader } from "../shell/LiteLayout";
import { TradeDrawer } from "../ui/TradeDrawer";
import { TradeList } from "../ui/TradeList";
import { Pnl, Segmented } from "../ui/ui";

type Outcome = "all" | "win" | "loss";

// Même export CSV que le widget « Trades » de l'app, sur les trades affichés.
function downloadCsv(trades: LiteTrade[]) {
  const csv = exportTradesToCSV(trades.map(t => t.raw));
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `trades_${dayKey(new Date())}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function matches(trade: LiteTrade, q: string) {
  return trade.symbol.toLowerCase().includes(q)
    || trade.note.toLowerCase().includes(q)
    || trade.tags.some(tag => tag.name.toLowerCase().includes(q));
}

export default function LiteJournalPage() {
  const t = useTranslation().lite;
  const tj = t.journal;
  const tAll = useTranslation();
  const { loading, fmt, trades: allTrades, scopedTrades, filteredTrades, refetchAll } = useLiteData();
  const { toast } = useToast();
  const deleteTradesFn = useAction(deleteTrades);
  // Mode sélection : suppression groupée, comme la sélection du tableau de trades Advanced.
  const [selection, setSelection] = useState<Set<string> | null>(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const toggle = (ids: string[], checked: boolean) => setSelection(prev => {
    const next = new Set(prev ?? []);
    for (const id of ids) { if (checked) next.add(id); else next.delete(id); }
    return next;
  });
  const bulkDelete = async () => {
    const ids = [...(selection ?? [])];
    setConfirmBulk(false);
    if (!ids.length) return;
    try {
      await deleteTradesFn({ ids });
      trackBulkTradeDeleted(ids.length);
      toast({ title: tAll.journal.bulkDeleteSuccess.replace("{count}", String(ids.length)) });
      setSelection(null);
      refetchAll();
    } catch (error: any) {
      toast({ title: tAll.common.error, description: error?.message || tAll.journal.bulkDeleteError, variant: "destructive" });
    }
  };
  const { openAddTrade, openImportCsv } = useLiteActions();
  const [params, setParams] = useSearchParams();
  const day = params.get("jour");
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [outcome, setOutcome] = useState<Outcome>("all");

  const trades = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Un jour choisi depuis un calendrier prime sur le filtre de période.
    const base = day ? scopedTrades.filter(x => dayKey(x.openedAt) === day) : filteredTrades;
    return base.filter(x => {
      // Gagnants / perdants : seuls les trades clôturés ont un résultat.
      if (outcome === "win" && (!x.closed || net(x) <= 0)) return false;
      if (outcome === "loss" && (!x.closed || net(x) > 0)) return false;
      return !q || matches(x, q);
    });
  }, [filteredTrades, scopedTrades, day, query, outcome]);

  const s = summarize(trades);
  const clearDay = () => {
    const next = new URLSearchParams(params);
    next.delete("jour");
    setParams(next);
  };

  const actions = (
    <div className="flex items-center gap-2 shrink-0">
      {allTrades.length > 0 && (
        <Button variant={selection ? "secondary" : "ghost"} size="sm" onClick={() => setSelection(sel => (sel ? null : new Set()))} aria-label={tj.select}>
          <CheckSquare className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{selection ? tj.selectDone : tj.select}</span>
        </Button>
      )}
      {allTrades.length > 0 && (
        <Button variant="ghost" size="sm" onClick={() => downloadCsv(trades)} disabled={!trades.length} title={tj.exportHint} aria-label={tj.export}>
          <Download className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{tj.export}</span>
        </Button>
      )}
      <Button variant="outline" size="sm" onClick={openImportCsv} aria-label={tj.import}>
        <FileUp className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{tj.import}</span>
      </Button>
      <Button size="sm" onClick={openAddTrade} aria-label={tj.addTrade}>
        <Plus className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">{tj.addTrade}</span>
      </Button>
    </div>
  );

  return (
    <>
      <PageHeader title={tj.title} subtitle={tj.subtitle} actions={actions} filters={allTrades.length ? "full" : false} />
      <PageBody>
        {loading ? (
          <p className="text-sm text-muted-foreground py-10 text-center">{t.common.loading}</p>
        ) : !allTrades.length ? (
          <EmptyStart icon={BookOpen} title={tj.emptyTitle} text={tj.emptyText} />
        ) : (
          <>
            <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-3">
              <div className="relative flex-1 min-w-0 md:min-w-[240px] md:max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 pointer-events-none" />
                <Input className="pl-9" placeholder={tj.searchPlaceholder} aria-label={tj.searchLabel} value={query} onChange={e => setQuery(e.target.value)} />
              </div>
              <div className="flex flex-wrap items-center gap-3">
                {day && (
                  <button type="button" onClick={clearDay} aria-label={tj.clearDay} title={tj.clearDay} className="h-9 inline-flex items-center gap-2 rounded-xl border border-primary/40 bg-primary/10 px-3 text-sm text-foreground hover:bg-primary/15">
                    {fmt.dateLong(`${day}T12:00:00`)} <X size={14} />
                  </button>
                )}
                <Segmented<Outcome>
                  value={outcome}
                  onChange={setOutcome}
                  options={[{ id: "all", label: tj.outcome.all }, { id: "win", label: tj.outcome.win }, { id: "loss", label: tj.outcome.loss }]}
                />
              </div>
              <div className="md:ml-auto flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                <span className="text-muted-foreground">{t.common.trades(trades.length)}</span>
                <span className="text-muted-foreground font-mono tabular-nums">{tj.winners(fmt.pct(s.winRate))}</span>
                <Pnl value={s.netPnl} className="font-semibold" />
              </div>
            </div>
            {/* La clé remet la pagination à zéro quand les filtres changent. */}
            <TradeList
              key={`${day ?? ""}|${outcome}|${query.trim()}`}
              trades={trades}
              onSelect={x => setSelected(x.id)}
              selectedId={selected ?? undefined}
              selection={selection ?? undefined}
              onToggle={toggle}
            />
            {selection && (
              <div className="sticky bottom-20 lg:bottom-4 z-20 mr-0 lg:mr-20 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card/95 backdrop-blur px-4 py-3 shadow-lg">
                <span className="text-sm">{tj.selectedCount(selection.size)}</span>
                <div className="flex items-center gap-2">
                  {selection.size > 0 && <Button variant="ghost" size="sm" onClick={() => setSelection(new Set())}>{tj.clearSelection}</Button>}
                  <Button variant="destructive" size="sm" disabled={!selection.size} onClick={() => setConfirmBulk(true)}>
                    <Trash2 className="h-4 w-4 mr-1.5" />{tAll.journal.deleteTrade}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </PageBody>
      <TradeDrawer tradeId={selected} onClose={() => setSelected(null)} />
      <Dialog open={confirmBulk} onOpenChange={setConfirmBulk}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tAll.journal.confirmBulkDelete.replace("{count}", String(selection?.size ?? 0))}</DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmBulk(false)}>{tAll.common.cancel}</Button>
            <Button variant="destructive" onClick={bulkDelete}>{tAll.journal.deleteTrade}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
